import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { supabase } from "./lib/supabaseClient";
import { JUNGNA_TABLES } from "./lib/jungnaTables";
import ProductDetails from "./pages/ProductDetails";
import AdminPage from "./pages/AdminPage";
import LoginPage from "./pages/LoginPage";
import OrderPayment from "./pages/OrderPayment";
import OrderComplete from "./pages/OrderComplete";
import styled from "styled-components";
import ScrollToTop from "./ScrollTotop";
import ProductDetailsPage from "./pages/product/[product_id]";
import { ProductProvider } from "./context/ProductContext";
import NotFound from "./pages/NotFound";
import DesktopBlocked from "./pages/DesktopBlocked";

const AppContainer = styled.div`
  width: 100%;
  max-width: 100vw;
  overflow-x: hidden;
`;

// 정의된 라우트가 아닌 경우만 URL 매핑 처리를 시도하는 컴포넌트
const CustomRouteHandler: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const processCustomRoute = async () => {
      console.log("CustomRouteHandler - 경로 처리:", location.pathname);

      // 이미 정의된 경로는 처리하지 않음
      if (location.pathname === "/" || 
          location.pathname === "/admin" || 
          location.pathname === "/404" ||
          location.pathname.startsWith("/product/")) {
        console.log("정의된 경로이므로 처리하지 않음:", location.pathname);
        return;
      }

      try {
        console.log("Supabase 쿼리 실행", location.pathname);
        const { data: mapping } = await supabase
          .from(JUNGNA_TABLES.urlMappings)
          .select("*")
          .eq("url", location.pathname)
          .eq("is_active", true)
          .single();

        console.log("URL 매핑 데이터", mapping);

        if (mapping && mapping.product_id) {
          console.log("상품 페이지로 리다이렉션:", mapping.product_id);
          navigate(`/product/${mapping.product_id}`, { replace: true });
        } else {
          console.log("매핑 없음, 404로 이동");
          navigate("/404", { replace: true });
        }
      } catch (error) {
        console.error("URL 매핑 에러", error);
        navigate("/404", { replace: true });
      }
    };

    processCustomRoute();
  }, [location.pathname, navigate]);

  return null;
};

const App: React.FC = () => {
  const isMobileEnv = () => {
    if (typeof window === "undefined") return true;
    const ua = navigator.userAgent || navigator.vendor || (window as any).opera || "";
    const uaMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua);
    const narrowViewport = window.innerWidth <= 820; // 태블릿 포함 허용치
    return uaMobile || narrowViewport;
  };

  if (!isMobileEnv()) {
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    const isAdminRoute = path.startsWith("/admin");
    if (!isAdminRoute) {
      return (
        <AppContainer>
          <DesktopBlocked />
        </AppContainer>
      );
    }
  }

  return (
    <AppContainer>
      <ProductProvider>
        <Router>
          <ScrollToTop />
          <Routes>
            {/* 정의된 경로들 */}
            <Route path="/" element={<ProductDetails />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/product/:product_id" element={<ProductDetailsPage />} />
            <Route path="/product/:product_id/order-payment" element={<OrderPayment />} />
            <Route path="/product/:product_id/order-complete" element={<OrderComplete />} />
            <Route path="/product/:product_id/login" element={<LoginPage />} />
            <Route path="/404" element={<NotFound />} />
            
            {/* 정의되지 않은 모든 경로는 CustomRouteHandler로 처리 */}
            <Route path="*" element={<CustomRouteHandler />} />
          </Routes>
        </Router>
      </ProductProvider>
    </AppContainer>
  );
};

export default App;
